export const evaluateOperator = (operator, fieldValue, conditionValue) => {
  const op = (operator || '').replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  const isEmpty =
    fieldValue === undefined ||
    fieldValue === null ||
    fieldValue === '' ||
    (Array.isArray(fieldValue) && fieldValue.length === 0);
  const value = Array.isArray(fieldValue) ? fieldValue.join(',') : String(fieldValue ?? '');
  const condition = String(conditionValue ?? '');

  switch (op) {
    case 'equals':       return value === condition;
    case 'notEquals':    return value !== condition;
    case 'contains':     return value.toLowerCase().includes(condition.toLowerCase());
    case 'notContains':  return !value.toLowerCase().includes(condition.toLowerCase());
    case 'startsWith':   return value.toLowerCase().startsWith(condition.toLowerCase());
    case 'endsWith':     return value.toLowerCase().endsWith(condition.toLowerCase());
    case 'greaterThan':  return !isEmpty && parseFloat(value) > parseFloat(condition);
    case 'lessThan':     return !isEmpty && parseFloat(value) < parseFloat(condition);
    case 'isEmpty':      return isEmpty;
    case 'isNotEmpty':   return !isEmpty;
    case 'isChecked':    return !isEmpty;
    case 'isNotChecked': return isEmpty;
    default:             return false;
  }
};

export const buildNameValueMap = (fields, formValues, { keyPreference = 'id' } = {}) => {
  const values = {};
  for (const field of (fields || [])) {
    const canonicalKey = keyPreference === 'name'
      ? field?.name || field?.id
      : field?.id || field?.name;
    const value = canonicalKey ? formValues?.[canonicalKey] : undefined;
    if (field?.name) values[field.name] = value;
    if (field?.id) values[field.id] = value;
  }
  return values;
};

export const computeFieldState = (field, values) => {
  let visible = true;
  let required = field?.required || false;
  let disabled = false;
  let depError = null;

  if (field?.hasVisibilityLogic && field?.visibilitySourceField) {
    const matches = evaluateOperator(
      field?.visibilityOperator || 'equals',
      values[field.visibilitySourceField],
      field?.visibilityValue || '',
    );
    visible = field?.visibilityAction === 'hide' ? !matches : matches;
  }

  for (const dependency of (field?.dependencies || [])) {
    if (!dependency?.sourceField) continue;
    const matches = evaluateOperator(
      dependency?.operator || 'equals',
      values[dependency.sourceField],
      dependency?.value || '',
    );
    if (!matches) continue;
    switch (dependency.action) {
      case 'hide':     visible = false; break;
      case 'show':     visible = true; break;
      case 'require':  required = true; break;
      case 'disable':  disabled = true; break;
      case 'setError': depError = dependency?.errorMessage || 'This field has an error'; break;
      default: break;
    }
  }

  return { visible, required, disabled, depError };
};

export const evaluateFieldLogicRules = (logicRules, formValues) => {
  const fieldOverrides = {};
  for (const rule of (logicRules || [])) {
    if (!rule?.enabled || rule?.targetType === 'page') continue;
    const conditions = rule?.conditions || [];
    if (conditions.length === 0 || !rule?.targetFieldId) continue;
    const results = conditions.map(condition =>
      !!condition?.fieldId &&
      evaluateOperator(
        condition?.operator || 'equals',
        formValues?.[condition.fieldId],
        condition?.value || '',
      )
    );
    const matches = rule?.matchType === 'any' ? results.some(Boolean) : results.every(Boolean);
    if (!matches) continue;

    if (!fieldOverrides[rule.targetFieldId]) fieldOverrides[rule.targetFieldId] = {};
    const target = fieldOverrides[rule.targetFieldId];
    switch (rule?.action) {
      case 'show':      target.visible = true; break;
      case 'hide':      target.visible = false; break;
      case 'require':   target.required = true; break;
      case 'unrequire': target.required = false; break;
      case 'enable':    target.disabled = false; break;
      case 'disable':   target.disabled = true; break;
      default: break;
    }
  }
  return fieldOverrides;
};

export const evaluatePageLogicRules = (logicRules, formValues) => {
  const skippedPageIds = new Set();
  const jumpTargetPageIds = [];
  for (const rule of (logicRules || [])) {
    if (!rule?.enabled || rule?.targetType !== 'page') continue;
    const conditions = rule?.conditions || [];
    if (conditions.length === 0) continue;
    const results = conditions.map(condition =>
      !!condition?.fieldId &&
      evaluateOperator(
        condition?.operator || 'equals',
        formValues?.[condition.fieldId],
        condition?.value || '',
      )
    );
    const matches = rule?.matchType === 'any' ? results.some(Boolean) : results.every(Boolean);
    if (!matches || !rule?.targetPageId) continue;
    if (rule?.action === 'skip_page') skippedPageIds.add(rule.targetPageId);
    if (rule?.action === 'jump_to_page' && !jumpTargetPageIds.includes(rule.targetPageId)) {
      jumpTargetPageIds.push(rule.targetPageId);
    }
  }
  return { skippedPageIds, jumpTargetPageIds };
};

export const resolveNextPageIndex = (
  steps,
  currentPageIndex,
  { skippedPageIds = new Set(), jumpTargetPageIds = [] } = {},
) => {
  const pages = Array.isArray(steps) ? steps : [];
  const lastPageIndex = pages.length - 1;
  if (lastPageIndex < 0 || currentPageIndex >= lastPageIndex) return currentPageIndex;
  let nextPageIndex = currentPageIndex + 1;
  for (const targetPageId of jumpTargetPageIds) {
    const targetIndex = pages.findIndex(page => page?.id === targetPageId);
    if (targetIndex > currentPageIndex) {
      nextPageIndex = targetIndex;
      break;
    }
  }
  while (nextPageIndex < lastPageIndex && skippedPageIds.has(pages[nextPageIndex]?.id)) {
    nextPageIndex += 1;
  }
  return nextPageIndex;
};

export const computeReachablePageIds = (steps, logicRules, formValues) => {
  const pages = Array.isArray(steps) ? steps : [];
  const reachablePageIds = new Set();
  if (pages.length === 0) return reachablePageIds;
  const availableValues = {};
  const visitedIndexes = new Set();
  let pageIndex = 0;
  while (pageIndex >= 0 && pageIndex < pages.length && !visitedIndexes.has(pageIndex)) {
    visitedIndexes.add(pageIndex);
    const page = pages[pageIndex];
    if (page?.id) reachablePageIds.add(page.id);
    for (const fieldId of (page?.fieldIds || [])) {
      if (Object.prototype.hasOwnProperty.call(formValues || {}, fieldId)) {
        availableValues[fieldId] = formValues[fieldId];
      }
    }
    if (pageIndex >= pages.length - 1) break;
    const result = evaluatePageLogicRules(logicRules, availableValues);
    const nextPageIndex = resolveNextPageIndex(pages, pageIndex, result);
    if (nextPageIndex <= pageIndex) break;
    pageIndex = nextPageIndex;
  }
  return reachablePageIds;
};

export const computeEffectiveFieldState = (field, values, logicOverrides) => {
  const base = computeFieldState(field, values);
  const override = logicOverrides?.[field?.id] || {};
  return {
    visible: 'visible' in override ? override.visible : base.visible,
    required: 'required' in override ? override.required : base.required,
    disabled: 'disabled' in override ? override.disabled : base.disabled,
    depError: base.depError,
  };
};